import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getUserDeclarationDetail } from '../../services/flowApproval.service';
import {
	ApprovalDeclarationDetail,
	Detail,
} from '../../@types/Declarations.type';
import Step3 from './organism/Step3';
import { getCompleteMonth } from '../../shared/utils/dates.util';
import { CaretCircleLeft, ClockClockwise } from '@phosphor-icons/react';
import ROUTES from '../../shared/constants/routes';
import Text from '../../components/atoms/Text';
import Tag from '../../components/molecules/Tag';
import Step1 from './organism/Step1';
import Step2 from './organism/Step2';
import { Skeleton } from '@mui/material';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';

const DetailApproval = () => {
	const [searchParams] = useSearchParams();
	const fftId = searchParams.get('fftId');
	const viewHome = searchParams.get('viewHome');
	const viewLucy = searchParams.get('viewLucy');
	const [declarationDetail, setDeclarationDetail] =
		useState<ApprovalDeclarationDetail>();
	const [activeTab, setActiveTab] = useState(0);
	const [isLoading, setIsLoading] = useState(true);
	const navigate = useNavigate();

	const backApprovalDeclarations = () => {
		if (viewHome === 'true') navigate(ROUTES.ROOT);
		else if (viewLucy === 'true') navigate(ROUTES.SERVICE_STATUS);
		else navigate(ROUTES.TAXES);
	};

	const userProfile = useSelector((state: RootState) => state.profile);

	const incomes = declarationDetail?.income.find(
		(income) => 'platec_income' in income,
	)?.platec_income;
	const expenses = declarationDetail?.expense.find(
		(expense) => 'total_expense' in expense,
	)?.total_expense;
	const deductibles = declarationDetail?.deductibles.find(
		(deductible) => 'based_deductibles' in deductible,
	)?.based_deductibles;

	const getDeclarationDetail = async (fftId: number) => {
		try {
			setIsLoading(true);
			const response = await getUserDeclarationDetail(fftId);
			setDeclarationDetail(response);
		} catch (error) {
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		getDeclarationDetail(Number(fftId));
	}, [fftId]);

	const tabs = [
		{ id: 0, label: 'Ingresos y retenciones' },
		{ id: 1, label: 'Gastos y deducciones' },
	];

	if (isLoading) {
		return (
			<div className='w-full p-3 md:p-12 inline-flex flex-col justify-start items-center gap-6 mt-[16px]'>
				<div className='w-full flex justify-start items-start gap-2'>
					<div>
						<CaretCircleLeft
							weight='fill'
							color='var(--h-primary)'
							width={32}
							height={32}
							className='cursor-pointer hidden lg:block'
							onClick={() => backApprovalDeclarations()}
						/>
						<CaretCircleLeft
							weight='fill'
							color='var(--h-primary)'
							width={24}
							height={24}
							className='cursor-pointer block lg:hidden'
							onClick={() => backApprovalDeclarations()}
						/>
					</div>

					<div className='flex flex-col justify-start items-start'>
						<Skeleton
							width={200}
							height={24}
						/>
					</div>
				</div>
				<div className='w-full flex flex-col gap-4 md:max-w-[700px] md:justify-center'>
					<div className='self-stretch p-4 rounded-tr-lg rounded-bl-lg rounded-br-lg rounded-tl-lg flex flex-col justify-start items-start gap-2'>
						<Skeleton
							width='100%'
							height={40}
							sx={{ borderRadius: 8 }}
						/>

						<Skeleton
							width='100%'
							height={120}
							sx={{ borderRadius: 8 }}
						/>

						<Skeleton
							width='100%'
							height={240}
							sx={{ borderRadius: 8 }}
						/>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className='w-full p-3 md:p-12 inline-flex flex-col justify-start items-center gap-6 mt-[16px]'>
			<div className='w-full flex justify-start items-start gap-2'>
				<div>
					<CaretCircleLeft
						weight='fill'
						color='var(--h-primary)'
						width={32}
						height={32}
						className='cursor-pointer hidden lg:block'
						onClick={() => backApprovalDeclarations()}
					/>
					<CaretCircleLeft
						weight='fill'
						color='var(--h-primary)'
						width={24}
						height={24}
						className='cursor-pointer block lg:hidden'
						onClick={() => backApprovalDeclarations()}
					/>
				</div>
				<div className='flex flex-col justify-start items-start'>
					<Text
						size='body-3'
						weight='bold'
					>
						{getCompleteMonth(
							Number(declarationDetail?.precalculate_details?.[0]?.month),
						)}{' '}
						{declarationDetail?.precalculate_details?.[0]?.year}
					</Text>
				</div>
			</div>

			<div className='w-full flex flex-col gap-4 md:max-w-[700px] md:justify-center'>
				<div className='self-stretch p-4 bg-white rounded-tr-lg rounded-bl-lg rounded-br-lg rounded-tl-lg flex flex-col justify-start items-start gap-4'>
					<Tag
						label='Presentando tu declaración ante el SAT'
						type='contrast'
						caseVariant='success'
						iconRight={<ClockClockwise size={20} />}
						sx='!bg-h-primary-50 mb-4'
					/>
					<Step3
						viewApprove={false}
						amount={Number(
							declarationDetail?.precalculate_details?.[0].totalBalance,
						)}
						precalculate_details={
							declarationDetail?.precalculate_details[0] as Detail
						}
						user_id={userProfile?.user?.id}
					/>
				</div>
				<div className='self-stretch p-0 rounded-tr-lg rounded-bl-lg rounded-br-lg rounded-tl-lg flex flex-col justify-start items-start'>
					<div className='w-full flex justify-center'>
						{tabs.map((tab) => (
							<button
								key={tab.id}
								onClick={() => setActiveTab(tab.id)}
								className={`flex-1 p-4 justify-center transition-colors duration-200 rounded-tl-lg rounded-tr-lg ${
									activeTab === tab.id ? 'bg-white' : 'bg-transparent'
								}`}
							>
								<Text
									size='body-3'
									weight='bold'
									sx={`w-full justify-center ${
										activeTab === tab.id
											? '!text-h-primary-50'
											: '!text-gray-500 hover:!text-gray-700'
									}`}
								>
									{tab.label}
								</Text>
							</button>
						))}
					</div>
					<div className='w-full bg-white p-4 rounded-bl-lg rounded-br-lg'>
						{activeTab === 0 && (
							<div className='flex flex-col self-stretch gap-4 mt-2'>
								<Step1
									viewApprove={false}
									retentionIncomes={incomes?.retentionIncome}
									month={getCompleteMonth(
										Number(declarationDetail?.precalculate_details?.[0]?.month),
									)}
								/>
							</div>
						)}
						{activeTab === 1 && (
							<div className='flex flex-col self-stretch gap-4 mt-2'>
								<Step2
									viewApprove={false}
									deductibles={deductibles}
									expenses={expenses}
									month={getCompleteMonth(
										Number(declarationDetail?.precalculate_details?.[0]?.month),
									)}
								/>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default DetailApproval;
